import React, { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { CurrencyEur, CheckCircle, ArrowCounterClockwise, Download, CalendarBlank, UserCircle, ChartLineUp, Printer, FileCsv, FileXls } from '@phosphor-icons/react';

const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const pct = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')}%`;

const MAANDEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

export default function CommissiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isVerkoper = user?.role === 'verkoper';
  const macht = user?.machtigingen || {};
  // Verkopers met machtiging 'commissies_eigen_zien' krijgen eigen scope (backend filtert op verkoper_id).
  // Admins en gebruikers met 'commissies_beheren' zien alles (globale scope).
  const canManageAll = isAdmin || !!macht.commissies_beheren;
  const canSeeOwn = !!macht.commissies_eigen_zien;
  const hasAccess = canManageAll || (isVerkoper && canSeeOwn);
  // "verkoperMode" = ingelogd als verkoper met eigen-scope → verberg admin-features (betalen, andere verkopers)
  const verkoperMode = isVerkoper && !canManageAll;
  const nu = new Date();
  const [jaar, setJaar] = useState(nu.getFullYear());
  const [maand, setMaand] = useState(nu.getMonth() + 1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [betaalModal, setBetaalModal] = useState(null); // {item}
  const [rapport, setRapport] = useState(null);
  const [rapportBusy, setRapportBusy] = useState(false);
  const [detailVerkoper, setDetailVerkoper] = useState(null); // verkoper_id of null
  const [modus, setModus] = useState('maand'); // 'maand' | 'range'
  const eersteVanMaand = `${nu.getFullYear()}-${String(nu.getMonth()+1).padStart(2,'0')}-01`;
  const vandaag = nu.toISOString().slice(0, 10);
  const [van, setVan] = useState(eersteVanMaand);
  const [tot, setTot] = useState(vandaag);
  const [metCommissie, setMetCommissie] = useState(() => {
    // Voorkeur onthouden in localStorage zodat gebruiker niet elke keer moet klikken.
    try { const v = localStorage.getItem('commissies_met_commissie_v1'); return v === null ? true : v === '1'; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('commissies_met_commissie_v1', metCommissie ? '1' : '0'); } catch { /* quota */ }
  }, [metCommissie]);
  const [verkoperFilter, setVerkoperFilter] = useState(''); // '' = alle verkopers, anders verkoper_id
  const [verkopers, setVerkopers] = useState([]);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  // Laad verkopers-lijst éénmaal voor de filter-dropdown
  useEffect(() => {
    API.get('/verkopers').then(r => setVerkopers(r.data || [])).catch(() => setVerkopers([]));
  }, []);

  const load = useCallback(() => {
    if (!hasAccess) return;
    setBusy(true);
    // Bouw params op basis van modus
    const rapParams = new URLSearchParams();
    if (modus === 'range') {
      // Voor de CSV/PDF/Excel-exports: 'tot' is EXCLUSIEF — verhoog met 1 dag zodat einddatum INCLUSIEF is
      const d = new Date(tot);
      d.setDate(d.getDate() + 1);
      rapParams.set('van', van);
      rapParams.set('tot', d.toISOString().slice(0, 10));
    } else {
      rapParams.set('jaar', jaar);
      rapParams.set('maand', maand);
    }
    if (metCommissie) rapParams.set('met_commissie', 'true');
    if (verkoperFilter) rapParams.set('verkoper_id', verkoperFilter);
    Promise.all([
      API.get(`/commissies/overzicht?jaar=${jaar}&maand=${maand}`).catch(() => ({ data: null })),
      API.get(`/verkopers/rapport?${rapParams}`).catch(() => ({ data: null })),
    ]).then(([cRes, rRes]) => {
      setData(cRes.data);
      setRapport(rRes.data);
    }).finally(() => setBusy(false));
  }, [jaar, maand, modus, van, tot, metCommissie, verkoperFilter, hasAccess]);
  useEffect(() => { load(); }, [load]);

  // Bij selectie van één verkoper: klap diens detail automatisch open
  useEffect(() => {
    if (verkoperFilter && rapport?.items?.length === 1) {
      setDetailVerkoper(rapport.items[0].verkoper_id);
    } else if (!verkoperFilter) {
      setDetailVerkoper(null);
    }
  }, [verkoperFilter, rapport]);

  const buildExportQuery = () => {
    const p = new URLSearchParams();
    if (modus === 'range') {
      const d = new Date(tot); d.setDate(d.getDate() + 1);
      p.set('van', van); p.set('tot', d.toISOString().slice(0, 10));
    } else {
      p.set('jaar', jaar); p.set('maand', maand);
    }
    if (metCommissie) p.set('met_commissie', 'true');
    if (verkoperFilter) p.set('verkoper_id', verkoperFilter);
    return p;
  };
  const downloadRapportCsv = () => {
    const p = buildExportQuery();
    window.open(`${apiBase}/api/verkopers/rapport/csv?${p}`, '_blank');
  };
  const downloadRapportExcel = (detail = false) => {
    const p = buildExportQuery();
    if (detail) p.set('detail', 'true');
    window.open(`${apiBase}/api/verkopers/rapport/excel?${p}`, '_blank');
  };
  const openRapportPdf = (detail = false) => {
    setRapportBusy(true);
    const p = buildExportQuery();
    if (detail) p.set('detail', 'true');
    window.open(`${apiBase}/api/verkopers/rapport/pdf?${p}`, '_blank');
    setTimeout(() => setRapportBusy(false), 600);
  };

  const markBetaald = async ({ verkoper_id, bedrag, notitie, betaalwijze, betaald_op }) => {
    await API.post('/commissies/markeer-betaald', {
      verkoper_id,
      periode: data.periode,
      bedrag: Number(bedrag),
      notitie,
      betaalwijze,
      betaald_op,
    });
    setBetaalModal(null);
    load();
  };

  const undoBetaald = async (verkoper_id) => {
    if (!window.confirm('Uitbetaling-registratie verwijderen? (zet commissie terug als openstaand)')) return;
    await API.delete(`/commissies/markeer-betaald/${verkoper_id}/${data.periode}`);
    load();
  };

  const downloadCsv = () => {
    window.open(`${apiBase}/api/commissies/export-csv?jaar=${jaar}&maand=${maand}`, '_blank');
  };

  const vorigeMaand = () => { if (maand === 1) { setJaar(j => j - 1); setMaand(12); } else setMaand(m => m - 1); };
  const volgendeMaand = () => { if (maand === 12) { setJaar(j => j + 1); setMaand(1); } else setMaand(m => m + 1); };

  const jarenSelect = Array.from({ length: 5 }, (_, i) => nu.getFullYear() - 3 + i);

  if (!hasAccess) {
    return (
      <div className="max-w-6xl mx-auto p-4" data-testid="commissies-geen-toegang">
        <div className="flex items-center gap-2 mb-4">
          <CurrencyEur size={22} className="text-gray-400" weight="duotone" />
          <h1 className="text-xl font-bold text-gray-900">Commissies verkopers</h1>
        </div>
        {isVerkoper ? (
          <div className="p-6 bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            🔒 <strong>Geen toegang</strong> — verkopers kunnen geen commissie-overzicht van collega's bekijken.
            Uw eigen commissie per bestelbon vindt u terug in <a href="/verkoop-rapport" className="text-[#002FA7] font-bold hover:underline">Verkooprapport</a>.
          </div>
        ) : (
          <div className="p-6 bg-amber-50 border border-amber-200 text-amber-900 text-sm">
            🔒 <strong>Geen toegang</strong> — deze pagina is enkel beschikbaar voor beheerders of gebruikers met de machtiging <strong>"Commissies beheren"</strong>. Vraag een admin om deze machtiging toe te kennen in <em>Instellingen → Verkopers → Machtigingen</em>.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            <CurrencyEur size={26} weight="duotone" className="text-[#002FA7]" />
            Commissies verkopers
          </h1>
          <p className="text-xs text-gray-500 mt-1">Berekend op basis van bestelbonnen per verkoper — configuratie via Instellingen → Verkopers.</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Modus-switcher Maand / Vrije periode */}
          <div className="inline-flex items-center gap-0.5 bg-gray-100 border border-gray-200 rounded p-0.5" data-testid="commissies-modus">
            <button
              onClick={() => setModus('maand')}
              className={`text-xs font-bold px-2.5 py-1 rounded ${modus === 'maand' ? 'bg-white shadow text-[#002FA7]' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="modus-maand"
            >Maand</button>
            <button
              onClick={() => setModus('range')}
              className={`text-xs font-bold px-2.5 py-1 rounded ${modus === 'range' ? 'bg-white shadow text-[#002FA7]' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="modus-range"
            >Periode</button>
          </div>
          {modus === 'maand' ? (
            <>
              <button onClick={vorigeMaand} className="btn btn-secondary btn-sm" data-testid="commissies-vorige">◀</button>
              <div className="inline-flex items-center gap-1 bg-white border border-gray-200 px-2" style={{ minHeight: 36 }}>
                <CalendarBlank size={14} className="text-gray-400" />
                <select value={maand} onChange={e => setMaand(Number(e.target.value))} className="border-0 text-sm font-bold bg-transparent focus:outline-none" data-testid="commissies-maand">
                  {MAANDEN.map((lab, i) => <option key={i + 1} value={i + 1}>{lab}</option>)}
                </select>
                <select value={jaar} onChange={e => setJaar(Number(e.target.value))} className="border-0 text-sm font-bold bg-transparent focus:outline-none" data-testid="commissies-jaar">
                  {jarenSelect.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button onClick={volgendeMaand} className="btn btn-secondary btn-sm" data-testid="commissies-volgende">▶</button>
            </>
          ) : (
            <div className="inline-flex items-center gap-1.5 bg-white border border-gray-200 px-2" style={{ minHeight: 36 }}>
              <CalendarBlank size={14} className="text-gray-400" />
              <input type="date" value={van} max={tot} onChange={e => setVan(e.target.value)} className="border-0 text-xs font-bold bg-transparent focus:outline-none" data-testid="commissies-van" />
              <span className="text-gray-400 text-xs">→</span>
              <input type="date" value={tot} min={van} onChange={e => setTot(e.target.value)} className="border-0 text-xs font-bold bg-transparent focus:outline-none" data-testid="commissies-tot" />
            </div>
          )}
          <button onClick={downloadCsv} className="btn btn-secondary btn-sm inline-flex items-center gap-1" data-testid="commissies-csv">
            <Download size={13} /> Commissies CSV
          </button>
        </div>
      </div>

      {/* Admin-rapport: omzet/kost/winst/marge per verkoper */}
      {rapport && rapport.items && rapport.items.length > 0 && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="verkopers-rapport-sectie">
          <div className="px-4 py-3 bg-gradient-to-r from-green-700 to-emerald-600 text-white flex items-start justify-between flex-wrap gap-3">
            <div className="inline-flex items-center gap-2">
              <ChartLineUp size={22} weight="bold" />
              <div>
                <div className="text-sm font-bold">Volledig verkopers-rapport</div>
                <div className="text-[11px] opacity-90">{rapport.periode_label} · {rapport.totaal_bons} bestelbon(nen){metCommissie ? ' · met commissie' : ''}</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {/* Verkoper filter + Met commissie toggle */}
              <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                <label className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-2 py-1 rounded">
                  <UserCircle size={14} weight="bold" />
                  <select
                    value={verkoperFilter}
                    onChange={e => { setVerkoperFilter(e.target.value); setDetailVerkoper(null); }}
                    className="bg-transparent border-0 text-white text-xs font-bold focus:outline-none cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                    data-testid="verkoper-filter"
                  >
                    <option value="" style={{ color: '#111' }}>Alle verkopers</option>
                    {verkopers.map(v => (
                      <option key={v.id} value={v.id} style={{ color: '#111' }}>
                        {v.naam}{!v.actief ? ' (inactief)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 px-3 py-1 rounded cursor-pointer select-none" data-testid="met-commissie-toggle">
                  <span className={`relative inline-flex shrink-0 items-center transition-all`} style={{ width: 36, height: 20, borderRadius: 999, background: metCommissie ? '#FF6B0D' : 'rgba(255,255,255,0.3)' }}>
                    <span style={{ position: 'absolute', left: metCommissie ? 18 : 2, top: 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.18s' }} />
                  </span>
                  <span className="text-xs font-bold">{metCommissie ? 'MET' : 'ZONDER'} commissie</span>
                  <input type="checkbox" className="sr-only" checked={metCommissie} onChange={e => setMetCommissie(e.target.checked)} />
                </label>
              </div>
              <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                <button onClick={downloadRapportCsv} className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white font-bold text-xs px-2.5 py-1.5 rounded" data-testid="rapport-csv">
                  <FileCsv size={13} weight="bold" /> CSV
                </button>
                <button onClick={() => downloadRapportExcel(false)} className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white font-bold text-xs px-2.5 py-1.5 rounded" data-testid="rapport-excel">
                  <FileXls size={13} weight="bold" /> Excel
                </button>
                <button onClick={() => downloadRapportExcel(true)} className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white font-bold text-xs px-2.5 py-1.5 rounded" data-testid="rapport-excel-detail">
                  <FileXls size={13} weight="bold" /> Excel + detail
                </button>
                <button onClick={() => openRapportPdf(false)} className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white font-bold text-xs px-2.5 py-1.5 rounded" disabled={rapportBusy} data-testid="rapport-pdf">
                  <Printer size={13} weight="bold" /> PDF
                </button>
                <button onClick={() => openRapportPdf(true)} className="inline-flex items-center gap-1 bg-white text-green-800 font-bold text-xs px-2.5 py-1.5 rounded" disabled={rapportBusy} data-testid="rapport-pdf-detail">
                  <Printer size={13} weight="bold" /> PDF + detail
                </button>
              </div>
            </div>
          </div>
          {/* Totaal-balk */}
          <div className={`px-4 py-3 grid grid-cols-2 ${metCommissie ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3 border-b border-gray-200 bg-gray-50`}>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Omzet totaal</div>
              <div className="text-lg font-bold text-gray-900 font-mono">{fmt(rapport.totaal_omzet)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Kostprijs</div>
              <div className="text-lg font-bold text-red-600 font-mono">{fmt(rapport.totaal_kost)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Winst bruto</div>
              <div className="text-lg font-bold text-green-700 font-mono">{fmt(rapport.totaal_winst)}</div>
            </div>
            {metCommissie && (
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Commissie</div>
                <div className="text-lg font-bold text-orange-600 font-mono">{fmt(rapport.totaal_commissie)}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{metCommissie ? 'Netto na commissie' : 'Gem. marge'}</div>
              <div className="text-lg font-bold text-[#002FA7] font-mono">{metCommissie ? fmt(rapport.totaal_netto_na_commissie) : `${rapport.marge_pct.toFixed(1)}%`}</div>
            </div>
          </div>
          {/* Per-verkoper tabel */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase font-bold tracking-wider text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Verkoper</th>
                  <th className="text-right px-3 py-2">Bons</th>
                  <th className="text-right px-3 py-2">Omzet (excl)</th>
                  <th className="text-right px-3 py-2">Kost</th>
                  <th className="text-right px-3 py-2">Winst</th>
                  <th className="text-right px-3 py-2">Marge</th>
                  {metCommissie && <>
                    <th className="text-right px-3 py-2 bg-orange-50">Commissie</th>
                    <th className="text-right px-3 py-2 bg-blue-50">Netto</th>
                  </>}
                  <th className="text-right px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {rapport.items.map(it => {
                  const isExp = detailVerkoper === it.verkoper_id;
                  return (
                    <React.Fragment key={it.verkoper_id}>
                      <tr className={`border-t border-gray-100 ${it.aantal_bons === 0 ? 'text-gray-400' : 'hover:bg-green-50/50'}`} data-testid={`rapport-rij-${it.verkoper_id}`}>
                        <td className="px-3 py-2">
                          <span className="font-bold">{it.naam}</span>
                          {!it.actief && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-300 font-bold">INACTIEF</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono">{it.aantal_bons}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono">{fmt(it.omzet)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono text-red-600">{fmt(it.kost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono font-bold text-green-700">{fmt(it.winst)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-bold">{it.marge_pct.toFixed(1)}%</td>
                        {metCommissie && <>
                          <td className="px-3 py-2 text-right tabular-nums font-mono text-orange-600 bg-orange-50/50">
                            {it.commissie_bedrag > 0 ? fmt(it.commissie_bedrag) : <span className="text-gray-400 italic">—</span>}
                            {it.commissie_effectief_pct > 0 && <div className="text-[10px] text-gray-500">{it.commissie_effectief_pct.toFixed(2)}%</div>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-mono font-bold text-[#002FA7] bg-blue-50/50">{fmt(it.netto_winst_na_commissie || it.winst)}</td>
                        </>}
                        <td className="px-3 py-2 text-right">
                          {it.aantal_bons > 0 && (
                            <button
                              onClick={() => setDetailVerkoper(isExp ? null : it.verkoper_id)}
                              className="text-[#002FA7] hover:underline text-xs font-bold"
                              data-testid={`rapport-toon-detail-${it.verkoper_id}`}
                            >
                              {isExp ? 'Verberg' : 'Toon'} ({it.aantal_bons})
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExp && (
                        <tr>
                          <td colSpan={metCommissie ? 9 : 7} className="bg-gray-50 p-0">
                            <div className="p-3 space-y-3">
                              {/* KPI-micro rij */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                <div className="bg-white border border-gray-200 rounded p-2">
                                  <div className="text-[9px] uppercase font-bold tracking-wider text-gray-500">Gem. per bon</div>
                                  <div className="text-sm font-bold text-[#002FA7] font-mono">{fmt(it.gem_bon)}</div>
                                </div>
                                <div className="bg-white border border-gray-200 rounded p-2">
                                  <div className="text-[9px] uppercase font-bold tracking-wider text-gray-500">Status-verdeling</div>
                                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                                    {Object.entries(it.status_telling || {}).sort((a, b) => b[1] - a[1]).map(([st, n]) => (
                                      <span key={st} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700 uppercase tracking-wider">{st}: {n}</span>
                                    ))}
                                  </div>
                                </div>
                                <div className="bg-white border border-gray-200 rounded p-2">
                                  <div className="text-[9px] uppercase font-bold tracking-wider text-gray-500">Top-klant</div>
                                  <div className="text-xs font-bold truncate text-gray-900">{it.top_klanten?.[0]?.naam || '—'}</div>
                                  {it.top_klanten?.[0] && <div className="text-[10px] text-gray-500 font-mono">{fmt(it.top_klanten[0].omzet)} · {it.top_klanten[0].aantal} bons</div>}
                                </div>
                                <div className="bg-white border border-gray-200 rounded p-2">
                                  <div className="text-[9px] uppercase font-bold tracking-wider text-gray-500">Top-artikel</div>
                                  <div className="text-xs font-bold truncate text-gray-900">{it.top_artikels?.[0]?.omschr || '—'}</div>
                                  {it.top_artikels?.[0] && <div className="text-[10px] text-gray-500 font-mono">{it.top_artikels[0].aantal.toFixed(0)}× · {fmt(it.top_artikels[0].omzet)}</div>}
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Top-artikels */}
                                {it.top_artikels?.length > 0 && (
                                  <div className="bg-white border border-gray-200 rounded overflow-hidden">
                                    <div className="px-2 py-1.5 bg-amber-50 text-[10px] font-bold uppercase tracking-wider text-amber-800 border-b border-amber-200">🔥 Top {Math.min(5, it.top_artikels.length)} artikels (omzet)</div>
                                    <table className="w-full text-xs">
                                      <thead><tr className="text-[9px] text-gray-500 uppercase">
                                        <th className="text-left px-2 py-1">Code</th>
                                        <th className="text-left px-2 py-1">Omschr.</th>
                                        <th className="text-right px-2 py-1">Qty</th>
                                        <th className="text-right px-2 py-1">Omzet</th>
                                        <th className="text-right px-2 py-1">Winst</th>
                                      </tr></thead>
                                      <tbody>
                                        {it.top_artikels.slice(0, 5).map(a => (
                                          <tr key={a.code} className="border-t border-gray-100">
                                            <td className="px-2 py-1 font-mono">{a.code}</td>
                                            <td className="px-2 py-1 truncate" style={{ maxWidth: 140 }}>{a.omschr}</td>
                                            <td className="px-2 py-1 text-right tabular-nums">{a.aantal.toFixed(0)}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-mono">{fmt(a.omzet)}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-mono font-bold text-green-700">{fmt(a.winst)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {/* Top-klanten */}
                                {it.top_klanten?.length > 0 && (
                                  <div className="bg-white border border-gray-200 rounded overflow-hidden">
                                    <div className="px-2 py-1.5 bg-blue-50 text-[10px] font-bold uppercase tracking-wider text-blue-800 border-b border-blue-200">⭐ Top {Math.min(5, it.top_klanten.length)} klanten</div>
                                    <table className="w-full text-xs">
                                      <thead><tr className="text-[9px] text-gray-500 uppercase">
                                        <th className="text-left px-2 py-1">Klant</th>
                                        <th className="text-right px-2 py-1">Bons</th>
                                        <th className="text-right px-2 py-1">Omzet</th>
                                        <th className="text-right px-2 py-1">Winst</th>
                                      </tr></thead>
                                      <tbody>
                                        {it.top_klanten.slice(0, 5).map(k => (
                                          <tr key={k.naam} className="border-t border-gray-100">
                                            <td className="px-2 py-1 truncate" style={{ maxWidth: 180 }}>{k.naam}</td>
                                            <td className="px-2 py-1 text-right tabular-nums">{k.aantal}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-mono">{fmt(k.omzet)}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-mono font-bold text-green-700">{fmt(k.winst)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                              {/* Volledige bestelbon-lijst */}
                              {it.bons?.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded overflow-hidden">
                                  <div className="px-2 py-1.5 bg-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-700 border-b border-gray-200">📋 Alle bestelbonnen ({it.bons.length})</div>
                                  <table className="w-full text-xs">
                                    <thead><tr className="text-[9px] text-gray-500 uppercase">
                                      <th className="text-left px-2 py-1">Bestelbon</th>
                                      <th className="text-left px-2 py-1">Datum</th>
                                      <th className="text-left px-2 py-1">Klant</th>
                                      <th className="text-left px-2 py-1">Status</th>
                                      <th className="text-right px-2 py-1">Artikels</th>
                                      <th className="text-right px-2 py-1">Omzet</th>
                                      <th className="text-right px-2 py-1">Kost</th>
                                      <th className="text-right px-2 py-1">Winst</th>
                                      <th className="text-right px-2 py-1">Marge</th>
                                    </tr></thead>
                                    <tbody>
                                      {it.bons.map(b => (
                                        <tr key={b.id} className="border-t border-gray-100 hover:bg-blue-50/30">
                                          <td className="px-2 py-1 font-mono font-bold">{b.id}</td>
                                          <td className="px-2 py-1">{b.datum}</td>
                                          <td className="px-2 py-1 truncate" style={{ maxWidth: 180 }}>{b.klant_naam}</td>
                                          <td className="px-2 py-1 text-[10px] uppercase tracking-wider text-gray-500">{b.status}</td>
                                          <td className="px-2 py-1 text-right tabular-nums">{(b.aantal_artikels || 0).toFixed(0)}</td>
                                          <td className="px-2 py-1 text-right tabular-nums font-mono">{fmt(b.excl)}</td>
                                          <td className="px-2 py-1 text-right tabular-nums font-mono text-red-600">{fmt(b.kost)}</td>
                                          <td className="px-2 py-1 text-right tabular-nums font-mono font-bold text-green-700">{fmt(b.winst)}</td>
                                          <td className="px-2 py-1 text-right tabular-nums">{b.marge_pct.toFixed(1)}%</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#002FA7] text-white font-bold">
                  <td className="px-3 py-2">TOTAAL</td>
                  <td className="px-3 py-2 text-right tabular-nums">{rapport.totaal_bons}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(rapport.totaal_omzet)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(rapport.totaal_kost)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(rapport.totaal_winst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{rapport.marge_pct.toFixed(1)}%</td>
                  {metCommissie && <>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(rapport.totaal_commissie)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(rapport.totaal_netto_na_commissie)}</td>
                  </>}
                  <td className="px-3 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* KPI-tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white border-l-4 border-[#002FA7] p-3">
            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Totaal commissie</div>
            <div className="text-xl font-bold text-[#002FA7] mt-0.5 font-mono" data-testid="commissies-totaal">{fmt(data.totaal_commissie)}</div>
          </div>
          <div className="bg-white border-l-4 border-green-600 p-3">
            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Totale omzet</div>
            <div className="text-xl font-bold text-green-700 mt-0.5 font-mono">{fmt(data.totaal_omzet)}</div>
          </div>
          <div className="bg-white border-l-4 border-amber-500 p-3">
            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Aantal verkopers</div>
            <div className="text-xl font-bold text-amber-700 mt-0.5">{(data.items || []).filter(i => i.aantal_bestelbonnen > 0).length}</div>
          </div>
          <div className="bg-white border-l-4 border-gray-400 p-3">
            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Periode</div>
            <div className="text-xl font-bold text-gray-700 mt-0.5 capitalize">{data.periode_label}</div>
          </div>
        </div>
      )}

      {busy && <div className="text-center py-8 text-sm text-gray-500">Laden...</div>}

      {/* Lijst */}
      {data && !busy && (
        data.items.length === 0 ? (
          <div className="text-center py-14 text-sm text-gray-500 border border-dashed border-gray-300">
            Geen verkopers geconfigureerd. Voeg eerst verkopers toe via <a className="text-[#002FA7] hover:underline font-bold" href="/instellingen?tab=verkopers">Instellingen → Verkopers</a>.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 divide-y divide-gray-100" data-testid="commissies-lijst">
            {data.items.map(it => {
              const actief = it.config?.type && it.config.type !== 'uit';
              const cfgLabel = it.config?.type === 'vast' ? `Vast ${pct(it.config.vast_pct)} op ${it.basis_type === 'excl_btw' ? 'excl BTW' : 'incl BTW'}`
                : it.config?.type === 'getrapt' ? `Getrapt (${(it.config.getrapt || []).length} schijven) op ${it.basis_type === 'excl_btw' ? 'excl BTW' : 'incl BTW'}`
                : 'Geen commissie geconfigureerd';
              return (
                <div key={it.verkoper_id} className="p-4" data-testid={`commissie-rij-${it.verkoper_id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center shrink-0">
                      <UserCircle size={24} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-sm">{it.naam}</h3>
                        {!it.actief && <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-300 font-bold">INACTIEF</span>}
                        {it.betaald && <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-800 border border-green-300 font-bold inline-flex items-center gap-1"><CheckCircle size={11} weight="fill" /> BETAALD {it.betaald.betaald_op}</span>}
                        {!actief && <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-bold">GEEN COMMISSIE-CONFIG</span>}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5 italic">{cfgLabel}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2.5 text-xs">
                        <div>
                          <div className="text-[10px] uppercase font-bold text-gray-500">Bestelbonnen</div>
                          <div className="font-bold font-mono">{it.aantal_bestelbonnen}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold text-gray-500">Omzet {it.basis_type === 'excl_btw' ? 'excl' : 'incl'} BTW</div>
                          <div className="font-bold font-mono">{fmt(it.basis_bedrag)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold text-gray-500">Effectief %</div>
                          <div className="font-bold font-mono">{pct(it.effectief_pct)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase font-bold text-gray-500">Commissie</div>
                          <div className="font-bold font-mono text-[#002FA7] text-sm">{fmt(it.commissie_bedrag)}</div>
                        </div>
                      </div>
                      {/* Schijven-detail (bij getrapt) */}
                      {it.config?.type === 'getrapt' && it.schijven?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="text-[10px] uppercase font-bold text-gray-500 mb-1">Schijven toegepast</div>
                          <div className="flex flex-wrap gap-1.5">
                            {it.schijven.map((s, i) => (
                              <span key={i} className="text-[10px] font-mono bg-gray-50 border border-gray-200 px-1.5 py-0.5">
                                {s.tot !== null ? `tot ${fmt(s.tot)}` : 'rest'} × {s.pct}% = <strong>{fmt(s.commissie)}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 flex flex-col gap-1.5">
                      {actief && it.commissie_bedrag > 0 && !it.betaald && (
                        <button onClick={() => setBetaalModal({ item: it })} className="btn btn-primary btn-sm font-bold inline-flex items-center gap-1" data-testid={`commissie-markeer-${it.verkoper_id}`}>
                          <CheckCircle size={13} weight="bold" /> Markeer betaald
                        </button>
                      )}
                      {it.betaald && (
                        <button onClick={() => undoBetaald(it.verkoper_id)} className="btn btn-secondary btn-sm text-xs inline-flex items-center gap-1 text-red-600" data-testid={`commissie-undo-${it.verkoper_id}`}>
                          <ArrowCounterClockwise size={12} /> Ongedaan
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {betaalModal && <BetalingModal item={betaalModal.item} onCancel={() => setBetaalModal(null)} onSave={markBetaald} />}
    </div>
  );
}

function BetalingModal({ item, onCancel, onSave }) {
  const [bedrag, setBedrag] = useState(item.commissie_bedrag);
  const [betaalwijze, setBetaalwijze] = useState('overschrijving');
  const [notitie, setNotitie] = useState('');
  const [betaaldOp, setBetaaldOp] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try { await onSave({ verkoper_id: item.verkoper_id, bedrag, betaalwijze, notitie, betaald_op: betaaldOp }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 bg-[#002FA7] text-white">
          <h3 className="font-bold text-base inline-flex items-center gap-2">
            <CheckCircle size={18} weight="fill" /> Commissie uitbetalen aan {item.naam}
          </h3>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Bedrag (€)</label>
            <input type="number" step="0.01" className="field-input font-mono" value={bedrag} onChange={e => setBedrag(e.target.value)} data-testid="commissie-betaal-bedrag" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Betaald op</label>
            <input type="date" className="field-input" value={betaaldOp} onChange={e => setBetaaldOp(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Betaalwijze</label>
            <select className="field-input" value={betaalwijze} onChange={e => setBetaalwijze(e.target.value)}>
              <option value="overschrijving">Overschrijving</option>
              <option value="cash">Cash</option>
              <option value="loonstrook">Op loonstrook</option>
              <option value="andere">Andere</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Notitie (optioneel)</label>
            <textarea className="field-input" rows={2} value={notitie} onChange={e => setNotitie(e.target.value)} placeholder="bv. ref betaling, maand inbegrepen in salaris ..." />
          </div>
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex gap-2 justify-end">
          <button className="btn btn-secondary" onClick={onCancel}>Annuleren</button>
          <button className="btn btn-primary font-bold" onClick={submit} disabled={busy} data-testid="commissie-betaal-bevestig">
            {busy ? 'Bezig...' : 'Registreren als betaald'}
          </button>
        </div>
      </div>
    </div>
  );
}
